import React, { ForwardedRef, forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react'
import TableGrouping, { IColumnsGrouping } from '../../../components/table-grouping/TableGrouping'
import Autocomplete from '../../../components/input/autocomplete';
import { variantStandardStyle } from '../../../components/input/autocomplete/styleJS';
import TextArea from '../../../components/input/TextArea';
import { GroupButton } from '../../../components/GroupButton';
import SelectMultilineEmployeeDialog from '../../../components/dialogs/SelectMultilineEmployeeDialog';
import TextValidator from '../../../components/input/text-validator';
import { IShift } from '../../../shift/models/shiftModels';
import AppContext from '../../../../AppContext';
import { searchShift } from '../../../shift/services/shiftServices';
import { RESPONSE_STATUS_CODE, SEARCH_OBJECT_MAX_SIZE } from '../../../constants/moduleConsts';
import { toast } from 'react-toastify';
import useMultiLanguage from '../../../../hook/useMultiLanguage';
import { numberExceptThisSymbols } from '../../../utils/functionUtils';
import { ButtonGroup, Col, Dropdown, DropdownButton, Row } from 'react-bootstrap';
import { ExportWord, ITemplateExportWord } from '../../../components/exportWord';
import TemplateGiayBaoLamThemGio from '../word-templates/TemplateGiayBaoLamThemGio';
import { styleTemplateDexuatLamThemGio, styleTemplateGiayBaoCaoLamThemGio } from '../../constants/registerOvertimeConsts';
import TemplateDeXuatLamThemGio from '../word-templates/TemplateDeXuatLamThemGio';
import CustomTooltip from '../../../components/custom-tooltip';
import { KTSVG } from '../../../../../_metronic/helpers';

const RegisterOvertimeWords: ITemplateExportWord[] = [
  {
    type: "1",
    name: "Giấy báo làm thêm giờ",
    componentPreview: (dataPreview) => (
      <TemplateGiayBaoLamThemGio data={dataPreview} />
    ),
    style: styleTemplateGiayBaoCaoLamThemGio
  },
  {
    type: "2",
    name: "Đề xuất làm thêm giờ",
    componentPreview: (dataPreview) => (
      <TemplateDeXuatLamThemGio data={dataPreview} />
    ),
    style: styleTemplateDexuatLamThemGio
  }
];

const DanhSachNhanVien = React.memo(forwardRef((props: any, ref: ForwardedRef<any>) => {
    let { values, employeeInfos, isView } = props;
    const menuPortalTarget = document.body;
    const { setPageLoading } = useContext(AppContext);
    const { lang } = useMultiLanguage();

    const dataNhanVienRef = useRef<any>([]);
    const listCaTrucRef = useRef<any>([]);

    const [isOpenDialog, setIsOpenDialog] = useState<boolean>(false);
    const [dataNhanVien, setDataNhanVien] = useState<any[]>(employeeInfos)
    const [listCaTruc, setListCaTruc] = useState<IShift[]>([]);
    const [dataChecked, setDataChecked] = useState<any>([]);
    const [isShowDialogExportWord, setIsShowDialogExportWord] = useState<{ open: boolean, type: string | null }>({
        open: false,
        type: null
    });
    const [listDataPreview, setListDataPreview] = useState<any>([]);
    const [dataWord, setDataWord] = useState<any>({});

    dataNhanVienRef.current = dataNhanVien;
    listCaTrucRef.current = listCaTruc;

    useImperativeHandle(ref, () => ({
        getDataNhanVien: () => dataNhanVien
    }));

    const getListDataCaTruc = async (searchData?: any) => {
        try {
            setPageLoading(true);
            const { data } = await searchShift(SEARCH_OBJECT_MAX_SIZE);
            if (data?.code === RESPONSE_STATUS_CODE.SUCCESS) {
                setListCaTruc(data?.data?.content || []);
                return;
            }
            toast.warning(data?.message);
        } catch (err) {
            toast.error(lang("GENERAL.ERROR"));
        } finally {
            setPageLoading(false);
        }
    };

    useEffect(() => {
        getListDataCaTruc()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        const employeeCountMap: Record<string, number> = {};
    
        const mappedData = employeeInfos.map((employee: any) => {
            const employeeId = employee?.id || 'null';
    
            if (!employeeCountMap[employeeId]) {
                employeeCountMap[employeeId] = 1;
            } else {
                employeeCountMap[employeeId]++;
            }
    
            const suffix = employeeCountMap[employeeId];
    
            return {
                ...employee,
                uniquePrefix: `nhan-vien-${employeeId}-${suffix}`,
            };
        });
    
        setDataNhanVien(mappedData);
    }, [employeeInfos]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>, index: number, data: any) => {
        const { name, value } = event.target;
        let _data = [...dataNhanVienRef.current];
        const thoiGianBatDau = name === "thoiGianBatDau" ? value : (_data[index].thoiGianBatDau ?? null);
        const thoiGianKetThuc = name === "thoiGianKetThuc" ? value : (_data[index].thoiGianKetThuc ?? null);

        if (thoiGianBatDau && thoiGianKetThuc) {
            const tongSoGio = tinhKhoangThoiGian(thoiGianBatDau, thoiGianKetThuc);
            _data[index].tongSoGio = tongSoGio;
        }
        _data[index][name] = value;
        setDataNhanVien(_data);
    }

    const lamTronTheoMoc = (phut: number): number => {
        if (phut < 15) return 0;
        if (phut < 30) return 0.25;
        if (phut < 45) return 0.5;
        return 0.75;
      }

    const tinhKhoangThoiGian = (batDau: string | null, ketThuc: string | null): number => {
        if (!batDau || !ketThuc) return 0;
        const [bdGio, bdPhut] = batDau.split(':').map(Number);
        const [ktGio, ktPhut] = ketThuc.split(':').map(Number);

        const batDauPhut = bdGio * 60 + bdPhut;
        let ketThucPhut = ktGio * 60 + ktPhut;

        if (ketThucPhut < batDauPhut) {
            ketThucPhut += 24 * 60;
        }

        const tongPhut = ketThucPhut - batDauPhut;
        const gio = Math.floor(tongPhut / 60);
        const phut = tongPhut % 60;

        return gio + lamTronTheoMoc(phut);
    }

    const handleChangeTextArea = (value: any, name: string, index: number, data: any) => {
        let _data: any = [...dataNhanVienRef.current];
        _data[index][name] = value;
        setDataNhanVien(_data);
    }

    const handleChangeSelect = (name: string, value: any, index: number, data: any) => {
        let _data = [...dataNhanVienRef.current];
        switch (name) {
            case "caNghiBu": {
                _data[index] = {
                    ..._data[index],
                    caNghiBuId: value?.id,
                    caNghiBuText: value?.tenCa
                }
                setDataNhanVien(_data)
                break
            }
            default: {
                _data[index] = {
                    ..._data[index],
                    [name]: value,
                }
                setDataNhanVien(_data);
                break
            }
        }
    };

    const columns = useCallback((): IColumnsGrouping[]  => { 
        const columns: IColumnsGrouping[] =  [
        {
            name: "STT",
            field: "stt",
            render: (row: any, index: number, stt: number) => <span >{stt}</span>,
            isSticky: true,
        },
        {
            name: "Họ và tên",
            field: "employeeText",
            headerStyle: {
                minWidth: "170px"
            },
            cellStyle: {
                minWidth: "170px",
                textAlign: "left"
            },
            isSticky: true,
        },
        {
            name: "Công việc được giao",
            field: "congViec",
            isSticky: true,
            headerStyle: {
                minWidth: "200px"
            },
            cellStyle: {
                textAlign: "left"
            },
            render: (row: any, index: number, stt: number, data: any) => (
                <TextArea
                    name="congViec"
                    type="text"
                    value={row?.congViec}
                    rows="2"
                    onChange={(value: any, name: string) => handleChangeTextArea(value, name, index, data)}
                />
            )
        },
        {
            name: "Thời gian làm thêm",
            field: "",
            child: [
                {
                    name: "Ngày đăng ký làm thêm",
                    field: "ngayDkTruc",
                    headerStyle: {
                        minWidth: "80px"
                    },
                    render: (row: any, index: number, stt: number, data: any) => {
                        return (
                            <TextValidator
                                name="ngayDkTruc"
                                type="date"
                                value={row?.ngayDkTruc}
                                onChange={(value: React.ChangeEvent<HTMLInputElement>) => handleChange(value, index, data)}
                            />
                        )
                    }
                },
                {
                    name: "Thời gian bắt đầu",
                    field: "thoiGianBatDau",
                    headerStyle: {
                        minWidth: "80px"
                    },
                    render: (row: any, index: number, stt: number, data: any) => {
                        return (
                            <TextValidator
                                name="thoiGianBatDau"
                                type="time"
                                value={row?.thoiGianBatDau}
                                onChange={(value: React.ChangeEvent<HTMLInputElement>) => handleChange(value, index, data)}
                            />
                        )
                    }
                },
                {
                    name: "Thời gian kết thúc",
                    field: "thoiGianKetThuc",
                    headerStyle: {
                        minWidth: "80px"
                    },
                    render: (row: any, index: number, stt: number, data: any) => {
                        return (
                            <TextValidator
                                name="thoiGianKetThuc"
                                type="time"
                                value={row?.thoiGianKetThuc}
                                onChange={(value: React.ChangeEvent<HTMLInputElement>) => handleChange(value, index, data)}
                            />
                        )
                    }
                },
                {
                    name: "Tổng số giờ",
                    field: "tongSoGio",
                    headerStyle: {
                        minWidth: "80px"
                    },
                    render: (row: any, index: number, stt: number, data: any) => {
                        return (
                            <TextValidator
                                name="tongSoGio"
                                type="number"
                                onKeyDown={numberExceptThisSymbols}
                                value={row?.tongSoGio || ""}
                                onChange={(value: React.ChangeEvent<HTMLInputElement>) => handleChange(value, index, data)}
                            />
                        )
                    }
                }
            ]
        },
        {
            name: "Duyệt nghỉ bù",
            field: "",
            child: [
                {
                    name: "Ngày nghỉ bù",
                    field: "ngayNghiBu",
                    headerStyle: {
                        minWidth: "80px"
                    },
                    render: (row: any, index: number, stt: number, data: any) => {
                        return (
                            <TextValidator
                                name="ngayNghiBu"
                                type="date"
                                value={row?.ngayNghiBu}
                                onChange={(value: React.ChangeEvent<HTMLInputElement>) => handleChange(value, index, data)}
                            />
                        )
                    }
                },
                {
                    name: "Ca nghỉ bù",
                    field: "",
                    headerStyle: {
                        minWidth: "150px"
                    },
                    render: (row: any, index: number, stt: number, data: any) => {
                        let caNghiBu = row?.caNghiBuId ? {
                            id: row?.caNghiBuId,
                            tenCa: row?.caNghiBuText,
                        } : null;
                        return (
                            <Autocomplete
                                options={listCaTrucRef.current}
                                placeholder='Nhập'
                                value={caNghiBu}
                                getOptionLabel={(option: any) => option?.tenCa}
                                styles={variantStandardStyle("26px")}
                                menuPortalTarget={menuPortalTarget}
                                onChange={(value) => handleChangeSelect("caNghiBu", value, index, data)}
                            />
                        )
                    }
                },
            ]
        },

    ]

    if (isView) {
        columns.filter(column => column.field ===  "action")
    } else {
        columns.push(        {
            name: lang("TABLE.ACTION"),
            field: "action",
            headerStyle: {
                minWidth: "80px"
            },
            cellStyle: {
                textAlign: "center",
                verticalAlign: "middle"
            },
            render: (row: any) => (
                <CustomTooltip title={lang("DELETE.ROW")}>
                    <span className="cursor-pointer" onClick={() => deleteNhanVienByUniquePrefix(row.uniquePrefix)
                    }>
                        <KTSVG
                            className="color-red"
                            path="/media/icons/trash.svg"
                            svgClassName='spaces w-20 h-20'
                        />
                    </span>
                </CustomTooltip>
            )
        })
    }

    return columns;
}, [isView]);

    const handleSelect = (data: any[]) => {
        if(!data.length) {
            toast.warning("Bạn chưa chọn nhân viên nào!");
            return;
        }
        const listNhanVien: any[] = [...dataNhanVien];
        const employeeCountMap: Record<string, number> = {};
        
        data.forEach((item: any) => {
            const employeeId = item?.id || 'null';
            if (!employeeCountMap[employeeId]) {
                employeeCountMap[employeeId] = 1;
            } else {
                employeeCountMap[employeeId]++;
            }
        
            const suffix = employeeCountMap[employeeId];
        
            const itemNhanVien = {
                uniquePrefix: `nhan-vien-${employeeId}-${suffix}`,
                employeeId: item?.id || null,
                employeeText: item?.name || null,
                congViec: item?.congViec || null,
                ngayDkTruc: item?.ngayDkTruc || null,
                thoiGianBatDau: item?.thoiGianBatDau || null,
                thoiGianNghiBatDau: item?.thoiGianNghiBatDau || null,
                thoiGianNghiKetThuc: item?.thoiGianNghiKetThuc || null,
                thoiGianKetThuc: item?.thoiGianKetThuc || null,
                tongSoGio: item?.tongSoGio || null,
                ngayNghiBu: item?.ngayNghiBu || null,
                caNghiBuId: item?.caNghiBuId || null,
                caNghiBuText: item?.caNghiBuText || null,
            };
        
            listNhanVien.push(itemNhanVien);
        });
        setDataNhanVien(listNhanVien)
        handleClose();
    }

    const handleClose = () => {
        setIsOpenDialog(false);
    }

    const handleOpenDialog = () => {
        if (!values.phongBan?.id) {
            toast.warn("Vui lòng chọn phòng ban trước!");
            return;
        }
        setIsOpenDialog(true)
    }

    const deleteNhanVienByUniquePrefix = (uniquePrefixToDelete: string) => {
        setDataNhanVien((prevData: any[]) => 
            prevData.filter(nv => nv.uniquePrefix !== uniquePrefixToDelete)
        );
    };

    const groupByEmployee = (dataChecked: any) => {
        const grouped: any = {};
        dataChecked.forEach((item: any) => {
            const { employeeId, employeeText, ...congViec } = item;
            if (!grouped[employeeId]) {
                grouped[employeeId] = {
                    employeeId,
                    employeeText,
                    listCongViec: [],
                };
            }

            grouped[employeeId].listCongViec.push(congViec);
        });

        return Object.values(grouped);
    };

    return (
        <div className='pt-2'>
            <div className='d-flex justify-content-between py-2'>
                <p className='fw-bold text-uppercase fs-3 m-0'>Danh sách nhân viên</p>
                <div className="flex gap-2">
                    <DropdownButton
                        as={ButtonGroup}
                        title={lang("BTN.PRINT")}
                        className='spaces min-w-80'
                        onSelect={(eventKey: string | null) => {
                            if (!dataChecked.length) {
                                toast.warning("Vui lòng chọn nhân viên!");
                                return;
                            };
                            const newData = groupByEmployee(dataChecked)
                            setIsShowDialogExportWord({
                                open: true,
                                type: eventKey
                            });
                            setListDataPreview(newData);
                            setDataWord({
                                dataSelect: newData[0],
                                dataDangKy: values,
                            });
                        }}
                    >
                        <Dropdown.Item eventKey="1">Giấy báo làm thêm giờ</Dropdown.Item>
                        <Dropdown.Item eventKey="2">Đề xuất làm thêm giờ</Dropdown.Item>
                    </DropdownButton>
                    <GroupButton handleEvent={handleOpenDialog}>Thêm mới</GroupButton>
                </div>

                {
                    isOpenDialog &&
                    <SelectMultilineEmployeeDialog
                        open={isOpenDialog}
                        searchObject={{
                            phongBanIds: values.phongBan?.id
                        }}
                        selectedData={[]}
                        uniquePrefixSelect="employeeId"
                        handleClose={handleClose}
                        handleSelect={handleSelect}
                    />
                }
            </div>
            <TableGrouping
                className="table-custom"
                id="timeSheets"
                columns={columns()}
                data={dataNhanVien}
                prefixDataField="data"
                prefixTitleField="phongBanText"
                isSelect={true}
                dataChecked={dataChecked}
                setDataChecked={setDataChecked}
                uniquePrefix='uniquePrefix'
            />
            {isShowDialogExportWord.open && (
                <ExportWord
                    open={isShowDialogExportWord.open}
                    dataWord={dataWord}
                    isExport={false}
                    isPrint={true}
                    isPrintOption={{
                        printAll: listDataPreview.length > 1 ? true: false,
                        listData: listDataPreview.map((item: any) => {
                            return {
                                dataSelect: item,
                                dataDangKy: values
                            }
                        })
                    }}
                    handleClose={() => setIsShowDialogExportWord({
                        open: false,
                        type: null
                    })}
                    templateList={RegisterOvertimeWords.filter(word => word.type === isShowDialogExportWord.type) || []}
                    ComponentSearch={() =>
                        <Row>
                            <Col xl={6}>
                                <Autocomplete
                                    horizontal={true}
                                    lable='Chọn nhân viên để xem mẫu word'
                                    options={listDataPreview}
                                    getOptionLabel={(option) => option.employeeText}
                                    value={dataWord.dataSelect}
                                    onChange={(value) => setDataWord({
                                        dataSelect: value,
                                        dataDangKy: values,
                                    })}
                                />
                            </Col>
                        </Row>
                    }
                    getObj={{}}
                    searchFunction={() => { }}
                />
            )}
        </div>
    )
}))

export default DanhSachNhanVien